import { ReactNode } from "react";
import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PositionsTableHeader } from "@/services/backend/htz/position/components/position-row";
import { AddPositionDropdown } from "@/services/backend/htz/position/components/add-position-dropdown";
import {
  Item,
  PositionCatalog,
} from "@/services/backend/htz/position-catalog/types/position-catalog";
import {
  Position,
  positionErrorKey,
} from "@/services/backend/htz/position/types/position";
import { useResetFieldsOnChange } from "@/shared/lib/errors/err-validation-failed-context";

/**
 * PositionsTable frames the positions of an offer, order or confirmation:
 * the card, the header and the dropdown adding a position from the catalog.
 * The document renders its rows as children, usually {@link PositionRow}s
 * wired to its own mutations.
 */
export function PositionsTable({
  positions,
  catalog,
  onAdd,
  disabled,
  children,
}: {
  positions: Position[];
  catalog: PositionCatalog;
  onAdd: (item: Item) => void;
  disabled: boolean;
  children: ReactNode;
}) {
  useResetDerivedPositionFields(positions);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between">
          <span>{t("Leistungen")}</span>
          <AddPositionDropdown
            catalog={catalog}
            onAdd={onAdd}
            disabled={disabled}
          />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ETable>
          <PositionsTableHeader />
          <ETableBody>
            {positions.length === 0 && <TableEmptyRow colSpan={8} />}
            {children}
          </ETableBody>
        </ETable>
      </CardContent>
    </Card>
  );
}

export function PositionsTableSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between">
          <span>{t("Leistungen")}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ETable>
          <PositionsTableHeader />
          <ETableBody>
            <ETableRow>
              <ETableCell colSpan={8} className="animate-pulse text-center">
                {t("Lade Daten")}
              </ETableCell>
            </ETableRow>
          </ETableBody>
        </ETable>
      </CardContent>
    </Card>
  );
}

/**
 * useResetDerivedPositionFields resets the errors of the fields derived
 * from the input of a position: the totals derive from the unit count,
 * price and hours, and the number of air handling units is bounded by the
 * unit count.
 */
function useResetDerivedPositionFields(positions: Position[]) {
  useResetFieldsOnChange(
    Object.fromEntries(
      positions.flatMap((p) => [
        [positionErrorKey(p, "totalPrice"), p.totalPrice],
        [positionErrorKey(p, "totalHours"), p.totalHours],
        [
          positionErrorKey(p, "airHandlingUnits"),
          [p.unitCount, p.airHandlingUnits],
        ],
      ]),
    ),
  );
}
